import React, { useState, useEffect } from 'react';
import { usePWA } from '../../hooks/usePWA';
import playlistData from '../../data/playlist.json';
import './DownloadButton.css';

const DownloadButton = () => {
  const {
    isServiceWorkerRegistered,
    isDownloading,
    downloadProgress,
    cacheStatus,
    downloadAllMedia,
    getCacheStatus,
    clearCache,
    getDownloadProgressPercentage,
    isAllMediaCached,
    swError
  } = usePWA();

  const [isAllCached, setIsAllCached] = useState(false);

  // Check cache status on mount and when cache status changes
  useEffect(() => {
    if (isServiceWorkerRegistered) {
      getCacheStatus(playlistData);
    }
  }, [isServiceWorkerRegistered, getCacheStatus]);

  useEffect(() => {
    setIsAllCached(isAllMediaCached());
  }, [cacheStatus, isAllMediaCached]);

  // Listen for cache status refresh requests
  useEffect(() => {
    const handleRefreshCacheStatus = () => {
      console.log('[DownloadButton] Refreshing cache status after download completion');
      if (isServiceWorkerRegistered) {
        getCacheStatus(playlistData);
      }
    };

    window.addEventListener('refreshCacheStatus', handleRefreshCacheStatus);
    return () => {
      window.removeEventListener('refreshCacheStatus', handleRefreshCacheStatus);
    };
  }, [isServiceWorkerRegistered, getCacheStatus]);

  const handleDownloadClick = async () => {
    if (isDownloading) return;
    
    // Enhanced iPad diagnostics with visual feedback
    // const userAgent = navigator.userAgent;
    // const isIPad = /iPad/.test(userAgent);
    // const isChromeOnIPad = /chrome/i.test(userAgent) && isIPad;
    
    // if (isChromeOnIPad) {
    //   console.log('📱 [iPad] Download initiated on Chrome iPad');
      
    //   // Visual feedback for iPad users since console isn't accessible
    //   const statusDiv = document.createElement('div');
    //   statusDiv.id = 'ipad-debug-status';
    //   statusDiv.style.cssText = `
    //     position: fixed; 
    //     top: 10px; 
    //     right: 10px; 
    //     background: rgba(0,0,0,0.8); 
    //     color: white; 
    //     padding: 10px; 
    //     border-radius: 5px; 
    //     z-index: 10000; 
    //     font-size: 12px; 
    //     max-width: 300px;
    //     word-wrap: break-word;
    //   `;
    //   statusDiv.innerHTML = `
    //     <div>🔍 iPad Debug Info:</div>
    //     <div>SW Registered: ${isServiceWorkerRegistered ? '✅' : '❌'}</div>
    //     <div>SW Controller: ${navigator.serviceWorker?.controller ? '✅' : '❌'}</div>
    //     <div>SW Error: ${swError || 'None'}</div>
    //     <div>Cache API: ${'caches' in window ? '✅' : '❌'}</div>
    //     <div>IndexedDB: ${'indexedDB' in window ? '✅' : '❌'}</div>
    //     <div>🚀 Starting download...</div>
    //   `;
      
    //   // Remove existing debug div if present
    //   const existingDiv = document.getElementById('ipad-debug-status');
    //   if (existingDiv) existingDiv.remove();
      
    //   document.body.appendChild(statusDiv);
      
    //   // Auto-remove after 15 seconds
    //   setTimeout(() => {
    //     const debugDiv = document.getElementById('ipad-debug-status');
    //     if (debugDiv) debugDiv.remove();
    //   }, 15000);
    // }
    
    // Always attempt download - let downloadAllMedia handle service worker issues
    await downloadAllMedia(playlistData);
  };

  const handleClearCache = async () => {
    if (window.confirm('Are you sure you want to clear all downloaded content? This will free up storage space but you\'ll need to download again for offline use.')) {
      await clearCache();
      setIsAllCached(false);
    }
  };

  const getButtonIcon = () => {
    if (isDownloading) {
      return 'fas fa-download';
    }
    if (isAllCached) {
      return 'fas fa-check';
    }
    return 'fas fa-download';
  };

  const getButtonClass = () => {
    let className = 'download-btn-circular';
    if (isDownloading) className += ' downloading';
    if (isAllCached) className += ' downloaded';
    return className;
  };

  const getTotalFiles = () => {
    let total = 0;
    playlistData.playlists.forEach(playlist => {
      playlist.tracks.forEach(track => {
        total += 1; // audio file
        if (track.artwork_url) total += 1; // artwork
        if (track.XR_Scene) total += 1; // XR video
      });
    });
    return total;
  };

  const hasXRContent = () => {
    return playlistData.playlists.some(playlist => 
      playlist.tracks.some(track => track.XR_Scene)
    );
  };

  const isMobileDevice = () => {
    const userAgent = navigator.userAgent;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  };

  const shouldShowXRWarning = () => {
    return hasXRContent() && isMobileDevice();
  };

  const getProgressAngle = () => {
    const percentage = getDownloadProgressPercentage();
    return (percentage / 100) * 360;
  };

  const getErrorMessage = () => {
    if (swError) {
      if (swError.includes('not supported')) {
        return 'Service Worker not supported in this browser';
      }
      if (swError.includes('fetch')) {
        return 'Network error - check your connection';
      }
      if (swError.includes('development mode')) {
        return 'Offline features available in production build';
      }
      if (swError.includes('direct cache API')) {
        return 'Using direct cache API (offline features available)';
      }
      return `Service Worker error: ${swError}`;
    }
    return 'Offline mode not available';
  };

  // Enhanced diagnostic information for troubleshooting iPad issues
  const getDiagnosticInfo = () => {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isChromeOnIOS = /chrome/i.test(userAgent) && isIOS;
    const isSafariEngine = /^((?!chrome|android).)*safari/i.test(userAgent) && !/chrome/i.test(userAgent);
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasCacheAPI = 'caches' in window;
    const hasIndexedDB = 'indexedDB' in window;
    
    return {
      userAgent: userAgent.substring(0, 100),
      isIOS,
      isChromeOnIOS,
      isSafariEngine,
      hasServiceWorker,
      hasCacheAPI,
      hasIndexedDB,
      swRegistered: isServiceWorkerRegistered,
      swController: !!navigator.serviceWorker?.controller,
      swError
    };
  };

  // Add diagnostic logging for iPad debugging
  useEffect(() => {
    const diagnostics = getDiagnosticInfo();
    console.log('📱 [DownloadButton] Device Diagnostics:', diagnostics);
    
    // Special logging for Chrome on iPad
    if (diagnostics.isChromeOnIOS) {
      console.log('📱 [DownloadButton] Chrome on iOS detected - enhanced debugging enabled');
      
      // Test basic APIs
      setTimeout(async () => {
        try {
          if (diagnostics.hasCacheAPI) {
            const cache = await caches.open('test-cache');
            console.log('📱 [DownloadButton] Cache API test: ✅ Working');
            await caches.delete('test-cache');
          }
        } catch (cacheError) {
          console.error('📱 [DownloadButton] Cache API test: ❌ Failed', cacheError);
        }
        
        try {
          if (diagnostics.hasIndexedDB) {
            const request = indexedDB.open('test-db', 1);
            request.onsuccess = () => {
              console.log('📱 [DownloadButton] IndexedDB test: ✅ Working');
              indexedDB.deleteDatabase('test-db');
            };
            request.onerror = () => {
              console.error('📱 [DownloadButton] IndexedDB test: ❌ Failed');
            };
          }
        } catch (indexedDBError) {
          console.error('📱 [DownloadButton] IndexedDB test: ❌ Failed', indexedDBError);
        }
      }, 1000);
    }
  }, [isServiceWorkerRegistered]);

  return (
    <div className="download-button-container">
      {/* Progress/Status Modal */}
      {(isDownloading || isAllCached) && (
        <div className="download-modal">
          {isDownloading ? (
            <>
              <div className="modal-progress">{getDownloadProgressPercentage()}%</div>
              <div className="modal-files">
                {downloadProgress.successCount + downloadProgress.failedCount} / {downloadProgress.total} files
              </div>
              {downloadProgress.failedCount > 0 && (
                <div className="modal-failed">
                  {downloadProgress.failedCount} failed
                </div>
              )}
              {shouldShowXRWarning() && (
                <div className="modal-xr-warning">
                  <i className="fas fa-info-circle"></i>
                  <span>XR scenes may have limited offline functionality on mobile</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="modal-complete">Download complete</div>
              <div className="modal-files">
                {downloadProgress.successCount || getTotalFiles()} / {downloadProgress.total || getTotalFiles()} files
              </div>
              {shouldShowXRWarning() && (
                <div className="modal-xr-warning">
                  <i className="fas fa-info-circle"></i>
                  <span>XR scenes may have limited offline functionality on mobile</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Button Row - Clear Cache and Download */}
      <div className="button-row">
        {/* Cache management - show to left of download button when downloaded */}
        {isAllCached && (
          <button
            className="clear-cache-btn-circular"
            onClick={handleClearCache}
            title="Clear downloaded content to free up storage"
          >
            <i className="fas fa-trash"></i>
          </button>
        )}

        {/* Circular Download Button */}
        <div className="download-btn-wrapper">
        {/* Circular Progress Ring */}
        {isDownloading && (
          <svg className="progress-ring" width="50" height="50">
            <circle
              className="progress-ring-bg"
              cx="25"
              cy="25"
              r="22"
              fill="transparent"
              stroke="rgba(255, 255, 255, 0.2)"
              strokeWidth="2"
            />
            <circle
              className="progress-ring-fill"
              cx="25"
              cy="25"
              r="22"
              fill="transparent"
              stroke="#4FC3F7"
              strokeWidth="2"
              strokeDasharray={`${2 * Math.PI * 22}`}
              strokeDashoffset={`${2 * Math.PI * 22 * (1 - getDownloadProgressPercentage() / 100)}`}
              transform="rotate(-90 25 25)"
            />
          </svg>
        )}
        
        <button
          className={getButtonClass()}
          onClick={handleDownloadClick}
          disabled={isDownloading}
          title={
            isDownloading 
              ? 'Downloading content for offline use...' 
              : isAllCached 
                ? 'All content downloaded for offline use' 
                : 'Download all audio, video, and images for offline use'
          }
        >
          <i className={getButtonIcon()}></i>
        </button>
        </div>
      </div>

      {/* Service worker status indicator */}
      {!isServiceWorkerRegistered && !swError?.includes('direct cache API') && (
        <div className="service-worker-status">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{getErrorMessage()}</span>
        </div>
      )}
      
      {/* Fallback mode indicator */}
      {swError?.includes('direct cache API') && (
        <div className="service-worker-status" style={{ background: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', borderColor: 'rgba(76, 175, 80, 0.3)' }}>
          <i className="fas fa-info-circle"></i>
          <span>{getErrorMessage()}</span>
        </div>
      )}
    </div>
  );
};

export default DownloadButton; 